import { Modal } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { FfsLtaReport } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import { FfsAnalysisResult } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import {
  PartSpecificReportDataArgs,
  useFfsStrategy,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FfsMawpSectionProps } from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import { FfsPartFourCalculatedValues } from 'features/indicationTriage/cards/ffsCalc/part4/FfsCalculatedValuesSection'
import { FfsThicknessCriteriaSectionProps } from 'features/indicationTriage/cards/ffsCalc/part4/FfsThicknessCriteriaSection'
import { FfsFinalAcceptabilityCriteraSectionValues } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFinalAcceptabilityCriteraSection'
import { FlawSizeCriteriaCheckResult } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFlawSizeCriteriaSection'
import { FC, useMemo, useState } from 'react'
import { v4 } from 'uuid'

import { useSaveReport } from './ffsLtaPublishApi'
import { FfsLtaReportTable } from './FfsLtaReportTable'
import { FfsLevel, FfsPart } from './ffsTypes'
import { NewSavedReportOutcomeForm } from './NewSavedReportOutcomeForm'
import { ExcludeSetters } from './typeUtils'

export interface FfsReportPanelProps {
  // Part 4 specific
  calculatedValues?: FfsPartFourCalculatedValues
  thicknessCriteria?: Omit<FfsThicknessCriteriaSectionProps, 'loading'>
  // Part 5 specific
  flawSizeCriteriaCheck?: FlawSizeCriteriaCheckResult
  finalAcceptabilityCriteriaCheck?: FfsFinalAcceptabilityCriteraSectionValues
  mawpProps: Omit<
    ExcludeSetters<FfsMawpSectionProps>,
    'loading' | 'ffsLevel' | 'addLongitudinalStress' | 'showMawpCriteria'
  >
  ffsPart: FfsPart
  ffsLevel: FfsLevel
  reports: ReadonlyArray<FfsLtaReport>
  reportsLoading: boolean
  reloadReports: () => Promise<void>
  setDefaultReport: (id: string | undefined) => Promise<void>
  setSelectedScenarioId: (id: string | null) => void
  selectedScenarioId: string | null
  analysisResult: FfsAnalysisResult
}

export const FfsReportPanel: FC<FfsReportPanelProps> = ({
  flawSizeCriteriaCheck,
  calculatedValues,
  finalAcceptabilityCriteriaCheck,
  thicknessCriteria,
  mawpProps,
  ffsPart,
  ffsLevel,
  reports,
  reportsLoading,
  reloadReports,
  setDefaultReport,
  setSelectedScenarioId,
  selectedScenarioId,
  analysisResult,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { rolodexDelete } = useRolodexEdit()

  const {
    strategy: { scenarios: scenarioStrategy },
  } = useFfsStrategy()

  const filteredScenarios = useMemo(
    () => scenarioStrategy.filterScenarios(reports, ffsLevel),
    [scenarioStrategy, reports, ffsLevel]
  )

  const partSpecificReportArgs: PartSpecificReportDataArgs = useMemo(
    () => ({
      flawSizeCriteriaCheck,
      calculatedValues,
      finalAcceptabilityCriteriaCheck,
      thicknessCriteria,
    }),
    [
      flawSizeCriteriaCheck,
      calculatedValues,
      finalAcceptabilityCriteriaCheck,
      thicknessCriteria,
    ]
  )

  const { saveOutcome, isSaving } = useSaveReport(
    partSpecificReportArgs,
    mawpProps,
    ffsPart,
    ffsLevel
  )

  const [isSavePending, setIsSavePending] = useState(false)

  const handleNewReport = () => {
    setIsModalOpen(true)
  }

  const handleDeleteReport = async (id: string) => {
    await rolodexDelete(id)
    await reloadReports()
  }

  const handleSaveReport = async (
    outcome: Parameters<typeof saveOutcome>[1]
  ) => {
    const reportId = `ffs-report-${v4().substring(0, 8)}`
    setIsSavePending(true)
    setIsModalOpen(false)
    await saveOutcome(reportId, outcome)
      .then(() => reloadReports())
      .finally(() => {
        setIsSavePending(false)
      })
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <FfsLtaReportTable
        ffsReports={filteredScenarios}
        loading={reportsLoading || isSavePending || isSaving}
        deleteReport={handleDeleteReport}
        newReport={handleNewReport}
        setDefaultReport={setDefaultReport}
        setSelectedScenarioId={setSelectedScenarioId}
        selectedScenarioId={selectedScenarioId}
      />

      <Modal
        title='New FFS Report Outcome'
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={600}>
        <NewSavedReportOutcomeForm
          commit={handleSaveReport}
          currentResult={analysisResult.resultType}
        />
      </Modal>
    </>
  )
}
